'use client';

import * as React from 'react';
import dynamic from 'next/dynamic';

// Dynamically import AceEditor to avoid SSR issues
const AceEditor = dynamic(
  async () => {
    const ace = await import('react-ace');

    // Import ace modes and themes
    await import('ace-builds/src-noconflict/ext-language_tools');
    await import('ace-builds/src-noconflict/mode-typescript');
    await import('ace-builds/src-noconflict/theme-tomorrow');
    await import('ace-builds/src-noconflict/theme-tomorrow_night');

    return ace;
  },
  {
    ssr: false,
    loading: () => (
      <div className="h-full w-full bg-background border rounded-md p-4 font-mono text-sm">
        <div className="animate-pulse">
          <div className="text-muted-foreground">Loading code editor...</div>
        </div>
      </div>
    )
  }
);

// Types
export type CodeLanguage = 'typescript';

interface DirectCodeEditorProps {
  className?: string;
  language?: CodeLanguage;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  showCursor?: boolean;
  appearance?: 'light' | 'dark';
}

// Direct Code Editor Component with optional dark appearance
function DirectCodeEditor({
  className,
  language = 'typescript',
  value = '',
  onChange,
  placeholder,
  readOnly = false,
  showCursor = false,
  appearance = 'light',
  ...props
}: DirectCodeEditorProps) {
  const [error, setError] = React.useState<string | null>(null);

  const handleEditorLoad = () => {
    console.log('✅ Ace Editor loaded successfully');
  };

  if (error) {
    return (
      <div className="h-full w-full bg-background border rounded-md p-4 font-mono text-sm">
        <div className="text-red-500">
          <div>// Error loading code editor</div>
          <div className="mt-2 text-xs">Error: {error}</div>
          <div className="mt-4 text-muted-foreground">
            <div>// Fallback: Static code display</div>
            <pre className="mt-2 whitespace-pre-wrap">{value}</pre>
          </div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div
        className={`h-full w-full border rounded-md overflow-hidden ${className || ''}`}
        {...props}
      >
        <AceEditor
          mode={language}
          theme={appearance === 'dark' ? 'tomorrow_night' : 'tomorrow'}
          value={value}
          onChange={readOnly ? undefined : onChange}
          onLoad={handleEditorLoad}
          readOnly={readOnly}
          name="direct-code-editor"
          width="100%"
          height="100%"
          fontSize={14}
          showPrintMargin={false}
          showGutter={true}
          highlightActiveLine={showCursor}
          setOptions={{
            enableBasicAutocompletion: !readOnly,
            enableLiveAutocompletion: !readOnly,
            enableSnippets: !readOnly,
            showLineNumbers: true,
            tabSize: 2,
            useWorker: false,
            wrap: true,
            fontFamily:
              "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
          }}
          placeholder={
            placeholder ||
            `// TypeScript Code Editor\nconst message: string = "Hello, World!";\nconsole.log(message);`
          }
          style={{
            fontFamily:
              "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
          }}
        />
      </div>
    );
  } catch (err: any) {
    console.error('❌ Ace Editor error:', err);
    setError(err.message || 'Failed to load code editor');
    return null; // This will trigger a re-render with error state
  }
}

export { DirectCodeEditor };
